
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { globalSubscriptions } from '@/data/subscriptionData';
import { getPaymentUrl } from '@/config/urls';

const SubscriptionPreview: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const subscriptionId = searchParams.get('id');

  React.useEffect(() => {
    if (!subscriptionId) {
      navigate('/dashboard');
    }
  }, [subscriptionId, navigate]);

  const subscription = globalSubscriptions.find(s => s.id === subscriptionId);

  const handleGoToPayment = () => {
    if (subscriptionId) {
      window.open(getPaymentUrl(subscriptionId), '_blank');
    }
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  if (!subscription) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-bold mb-4">Subscription Not Found</h1>
          <Button onClick={handleBackToDashboard}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={handleBackToDashboard} 
          className="mb-6 flex items-center"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
        </Button>
        
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h1 className="text-2xl font-bold mb-4">Subscription Preview</h1>
          <p className="text-gray-600 mb-6">
            This is how your payment page will appear to customers. You can test the payment flow by clicking "Go to Payment Page".
          </p>
          
          <div className="border rounded-lg p-6 mb-6 bg-gray-50">
            <h2 className="text-xl font-semibold mb-2">{subscription.name}</h2>
            <p className="text-gray-600 mb-4">{subscription.description}</p>
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium">Price:</span>
              <span>
                ${subscription.amount.toFixed(2)}
                <span className="text-sm text-gray-500">
                  /{subscription.interval}
                </span>
              </span>
            </div>
            {subscription.one_time_fee && (
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">One-time setup fee:</span>
                <span>${subscription.one_time_fee.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="font-medium">Type:</span>
              <span>
                Subscription ({subscription.interval})
              </span>
            </div>
          </div>
          
          <div className="border rounded-lg p-4 mb-6 bg-gray-50">
            <p className="text-sm text-gray-500 mb-2">Payment Link:</p>
            <div className="flex items-center">
              <code className="bg-gray-100 p-2 rounded flex-grow">
                {getPaymentUrl(subscriptionId)}
              </code>
              <Button 
                size="sm" 
                variant="outline" 
                className="ml-2"
                onClick={() => {
                  navigator.clipboard.writeText(getPaymentUrl(subscriptionId));
                }}
              >
                Copy
              </Button>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button onClick={handleGoToPayment}>
              Go to Payment Page
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPreview;
